import QueryString from "query-string";
import React from "react";
import NYIModal from "../components/NYIModal";
import PrizeTable from "../components/PrizeTable";
import TicketDetailsModal from "../components/TicketDetailsModal";
import { BASE_10, DOLLAR_IN_CENTS } from "../constants";
import IKenoDraw from "../interfaces/IKenoDraw";
import IPrizeTable from "../interfaces/IPrizeTable";
import ISelection from "../interfaces/ISelection";
import ITicket from "../interfaces/ITicket";
import { BONUS_OUTCOME_NAME } from "../settings";
import filterNumberSelections from "../utils/filterNumberSelections";
import getTicketDrawnNumbers from "../utils/getTicketDrawnNumbers";
import getTicketStatus from "../utils/getTicketStatus";
import getTicketStatusText from "../utils/getTicketStatusText";
import history from "../utils/history";

interface IProps {
    currentDraw: IKenoDraw;
    drawnNumbers: number[];
    pastDraws: {
        [key: string]: IKenoDraw | undefined;
    };
    prizeTable: IPrizeTable;
    selectedTicket: ITicket | undefined;
    tickets: ITicket[];
    onSelectTicket(ticket?: ITicket): void;
}

interface IState {
    detailsTicket?: ITicket;
    notYetImplemented: boolean;
    selectedTicket?: ITicket;
}

export default class TicketsSidebar extends React.Component<IProps, IState> {
    public state: IState = {
        notYetImplemented: false,
    };
    public componentDidMount(): void {
        const { tickets } = this.props;
        const selectedTicket = tickets[0];

        this.setState({
            selectedTicket,
        });
    }
    public render(): JSX.Element {
        const {
            selectedTicket,
            tickets,
            currentDraw,
        } = this.props;
        const {
            detailsTicket,
            notYetImplemented,
        } = this.state;

        tickets.sort((a: ITicket, b: ITicket): number => {

            // Sort on their live/upcoming/completed order
            const aState = this.getTicketSortState(a);
            const bState = this.getTicketSortState(b);
            if (aState !== bState) {
                return aState - bState;
            }

            // Sort by the draw id ascending
            const aDrawId = a.event ? parseInt(a.event.externalId, BASE_10) : 0;
            const bDrawId = b.event ? parseInt(b.event.externalId, BASE_10) : 0;
            if (aDrawId !== bDrawId) {
                return aDrawId - bDrawId;
            }

            // Sort by the ticket id ascending
            return parseInt(a.id, BASE_10) - parseInt(b.id, BASE_10);
        });

        return (
            <div className="tickets-sidebar">
                { notYetImplemented ? (
                    <NYIModal
                        onClose={ (): void => {
                            this.setState({ notYetImplemented: false });
                        } }
                    />
                ) : undefined }
                { detailsTicket ? (
                    <TicketDetailsModal
                        currentDraw={ currentDraw }
                        ticket={ detailsTicket }
                        onClose={ this.hideTicketDetails.bind(this) }
                        onReplay={ this.replayTicket.bind(this, detailsTicket) }
                    />
                ) : undefined }

                { tickets.map((ticket: ITicket): JSX.Element => {
                    const content = ticket === selectedTicket ? (
                        this.renderDetailedTicket(ticket, currentDraw)
                    ) : (
                        this.renderVagueTicket(ticket, currentDraw)
                    );

                    return (
                        <div
                            key={ ticket.id }
                            className="sidebar-wrapper"
                        >
                            <div className="sidebar-content">
                                { content }
                            </div>
                        </div>
                    );
                }) }
            </div>
        );
    }
    public renderDetailedTicket(ticket: ITicket, currentDraw: IKenoDraw): JSX.Element {
        const {
            onSelectTicket,
            pastDraws,
            prizeTable,
            drawnNumbers,
        } = this.props;
        const { selections } = ticket;
        const numberSelections = selections ? filterNumberSelections(selections) : [];
        const ticketDrawnNumbers = getTicketDrawnNumbers(ticket, currentDraw, drawnNumbers, pastDraws);

        const selectedNumbers = this.getNumbersFromSelections(numberSelections);

        const currentDrawId = currentDraw ? parseInt(currentDraw.id, BASE_10) : -1;
        const highlightPrizeWon = ticket.event ? (parseInt(ticket.event.externalId, BASE_10) <= currentDrawId) : false;

        let matches = 0;
        selectedNumbers.forEach((num: number) => {
            if (ticketDrawnNumbers.includes(num)) {
                matches++;
            }
        });

        return (
            <div key={ ticket.id } className="ticket">
                <div
                    className="clickable"
                    onClick={ onSelectTicket.bind(this, undefined) }
                >
                    { this.renderTicketHeading(ticket, currentDraw) }
                    <div className="toggle-icon"><i className="fa fa-sort-up"></i></div>
                </div>
                <div className="prizes">
                    <PrizeTable
                        highlightPrizeWon={ highlightPrizeWon }
                        multiplier={ (ticket.wager || 1) / DOLLAR_IN_CENTS }
                        matches={ matches }
                        selectionCount={ numberSelections.length }
                        prizeTable={ prizeTable }
                    />
                </div>
                <div className="actions">
                    <p>
                        <button
                            type="button"
                            className="styled-button outline"
                            onClick={ this.replayTicket.bind(this, ticket) }
                        >Replay Ticket</button>
                    </p>
                    <p>
                        <button
                            onClick={ this.showTicketDetails.bind(this, ticket) }
                            type="button"
                            className="link-button"
                        >View Ticket Details</button>
                    </p>
                </div>
            </div>
        );
    }
    public renderTicketHeading(ticket: ITicket, currentDraw: IKenoDraw): JSX.Element {
        const { drawnNumbers, pastDraws } = this.props;
        const selections = ticket.selections || [];
        const filtered = filterNumberSelections(selections);
        const visiblyDrawnNumbers = getTicketDrawnNumbers(ticket, currentDraw, drawnNumbers, pastDraws);

        filtered.sort((a: ISelection, b: ISelection): number => {
            const OFF_BOARD_NUMBER = 81;

            const aValue = (a && a.outcome) ? parseInt(a.outcome.name, BASE_10) : OFF_BOARD_NUMBER;
            const bValue = (b && b.outcome) ? parseInt(b.outcome.name, BASE_10) : OFF_BOARD_NUMBER;

            return aValue - bValue;
        });

        const drawId = ticket.event ? ticket.event.externalId : undefined;

        // Get the ticket status
        const ticketStatus = getTicketStatus(ticket, currentDraw);
        const ticketStatusText = getTicketStatusText(ticket, currentDraw);

        return (
            <div className="ticket-heading">
                <div className="ticket-title">
                    Ticket #{ ticket.id }
                    { drawId ? ` - Draw #${ drawId }` : "" }&nbsp;-&nbsp;
                    <span className={ticketStatus === "live" ? "active" : ""}>{ ticketStatusText }</span>
                </div>
                <div key="selections" className="styled-selections">
                    { filtered.map((sel: ISelection): JSX.Element => {
                        const { outcome } = sel;
                        const value = outcome ? parseInt(outcome.name, BASE_10) : 0;

                        const classes = ["selection"];
                        if (visiblyDrawnNumbers.includes(value)) {
                            classes.push("match");
                        }

                        return (
                            <span className={ classes.join(" ") } key={ sel.id }>
                                { outcome ? outcome.name : "" }
                            </span>
                        );
                    }) }
                </div>
            </div>
        );
    }
    public renderVagueTicket(ticket: ITicket, currentDraw: IKenoDraw): JSX.Element {
        const { onSelectTicket } = this.props;

        return (
            <div
                key={ ticket.id }
                onClick={ onSelectTicket.bind(this, ticket) }
                className="ticket clickable"
            >
                { this.renderTicketHeading(ticket, currentDraw) }
                <div className="toggle-icon"><i className="fa fa-sort-down"></i></div>
            </div>
        );
    }

    protected getNumbersFromSelections(selections: ISelection[]): number[] {
        const numbers: number[] = [];

        selections.forEach((selection: ISelection): void => {
            const { outcome } = selection;
            if (outcome) {
                const parsed = parseInt(outcome.name, BASE_10);
                if (!isNaN(parsed)) {
                    numbers.push(parsed);
                }
            }
        });

        return numbers;
    }
    protected hideTicketDetails(): void {
        this.setState({
            detailsTicket: undefined,
        });
    }
    protected replayTicket(ticket: ITicket): void {
        let { selections } = ticket;
        if (!selections) {
            selections = [];
        }

        const numbers = this.getNumbersFromSelections(selections);

        const bonus = selections ? selections.filter((sel: ISelection): boolean => {
            const { outcome } = sel;
            if (!outcome) {
                return false;
            }
            return outcome.name === BONUS_OUTCOME_NAME;
        }).length > 0 : false;

        const wager = ticket.wager;

        const params = {
            bonus: bonus ? 1 : 0,
            draws: 1,
            numbers: numbers.join(","),
            wager,
        };
        history.push(`/purchase/?${ QueryString.stringify(params) }`);
    }
    protected showTicketDetails(detailsTicket: ITicket): void {
        this.setState({
            detailsTicket,
        });
    }

    /**
     * Return a numeric value for sorting tickets by their state:
     * 1 = live
     * 2 = upcoming
     * 3 = completed
     *
     * @returns the number 1, 2, or 3
     */
    private getTicketSortState(ticket: ITicket): number {
        const { currentDraw } = this.props;
        const currentDrawId = parseInt(currentDraw.id, BASE_10);

        const LIVE = 1;
        const UPCOMING = 2;
        const COMPLETED = 3;

        // Assume completed if unknown
        if (!ticket.event) {
            return COMPLETED;
        }

        const ticketDrawId = parseInt(ticket.event.externalId, BASE_10);

        if (ticketDrawId === currentDrawId) {
            return LIVE;
        } else if (ticketDrawId > currentDrawId) {
            return UPCOMING;
        } else {
            return COMPLETED;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/TicketsSidebar.tsx